import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SiteProvider } from "@/context/SiteContext";
import { AppShell } from "@/components/shell/AppShell";
import { homeForRole } from "@/constants/modules";
import { Loader } from "@/components/common/Loader";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Production from "@/pages/Production";
import Sales from "@/pages/Sales";
import SalesOrderDetail from "@/pages/SalesOrderDetail";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Materials from "@/pages/Materials";
import WarehouseAsrs from "@/pages/WarehouseAsrs";
import Quality from "@/pages/Quality";
import Finance from "@/pages/Finance";
import Controlling from "@/pages/Controlling";
import Edi from "@/pages/Edi";
import Copilot from "@/pages/Copilot";
import Deliveries from "@/pages/Deliveries";
import Picking from "@/pages/Picking";
import MaterialDetail from "@/pages/MaterialDetail";
import PurchaseOrderDetail from "@/pages/PurchaseOrderDetail";
import SalesOrderForm from "@/pages/SalesOrderForm";
import Purchasing from "@/pages/Purchasing";
import VendorDetail from "@/pages/VendorDetail";
import PurchaseRequisitionForm from "@/pages/PurchaseRequisitionForm";
import BomDetail from "@/pages/BomDetail";
import RoutingDetail from "@/pages/RoutingDetail";
import InspectionLotDetail from "@/pages/InspectionLotDetail";
import ExecutiveSummary from "@/pages/ExecutiveSummary";
import Quotations from "@/pages/Quotations";
import FranceEInvoicing from "@/pages/FranceEInvoicing";
import Eudr from "@/pages/Eudr";
import EudrSupplier from "@/pages/EudrSupplier";
import UsTax from "@/pages/UsTax";
import TransactionCodes from "@/pages/TransactionCodes";
import TransportPlanning from "@/pages/TransportPlanning";
import DriverApp from "@/pages/DriverApp";
import WarehouseMonitor from "@/pages/WarehouseMonitor";
import OrderImport from "@/pages/OrderImport";
import StoreDirectory from "@/pages/StoreDirectory";
import MonthEndClose from "@/pages/MonthEndClose";
import PickConfirmations from "@/pages/PickConfirmations";
import PrintCenter from "@/pages/PrintCenter";
import DeliveryScheduling from "@/pages/DeliveryScheduling";
import WasteCompliance from "@/pages/WasteCompliance";
import Pim from "@/pages/Pim";
import SupportInbox from "@/pages/SupportInbox";
import Shop from "@/pages/Shop";
function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen bg-[#f5f6f7]"><Loader label="Authenticating…" /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  return <SiteProvider><AppShell>{children}</AppShell></SiteProvider>;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user && user !== false) return <Navigate to={homeForRole(user.role)} replace />;
  return <Login />;
}

function Home() {
  const { user } = useAuth();
  const dest = homeForRole(user?.role);
  if (dest !== "/") return <Navigate to={dest} replace />;
  return <Dashboard />;
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/eudr-supplier/:token" element={<EudrSupplier />} />
            <Route path="/" element={<Protected><Home /></Protected>} />
            <Route path="/executive" element={<Protected><ExecutiveSummary /></Protected>} />
            <Route path="/production" element={<Protected><Production /></Protected>} />
            <Route path="/bom/new" element={<Protected><BomDetail /></Protected>} />
            <Route path="/bom/:code" element={<Protected><BomDetail /></Protected>} />
            <Route path="/routing/new" element={<Protected><RoutingDetail /></Protected>} />
            <Route path="/routing/:code" element={<Protected><RoutingDetail /></Protected>} />
            <Route path="/sales" element={<Protected><Sales /></Protected>} />
            <Route path="/quotations" element={<Protected><Quotations /></Protected>} />
            <Route path="/einvoicing" element={<Protected><FranceEInvoicing /></Protected>} />
            <Route path="/eudr" element={<Protected><Eudr /></Protected>} />
            <Route path="/us-tax" element={<Protected><UsTax /></Protected>} />
            <Route path="/sales/new" element={<Protected><SalesOrderForm /></Protected>} />
            <Route path="/sales/:id/edit" element={<Protected><SalesOrderForm /></Protected>} />
            <Route path="/sales/:id" element={<Protected><SalesOrderDetail /></Protected>} />
            <Route path="/customers" element={<Protected><Customers /></Protected>} />
            <Route path="/customers/:id" element={<Protected><CustomerDetail /></Protected>} />
            <Route path="/materials" element={<Protected><Materials /></Protected>} />
            <Route path="/materials/:code" element={<Protected><MaterialDetail /></Protected>} />
            <Route path="/purchase-orders/:id" element={<Protected><PurchaseOrderDetail /></Protected>} />
            <Route path="/purchasing" element={<Protected><Purchasing /></Protected>} />
            <Route path="/requisitions/new" element={<Protected><PurchaseRequisitionForm /></Protected>} />
            <Route path="/requisitions/:id" element={<Protected><PurchaseRequisitionForm /></Protected>} />
            <Route path="/vendors/new" element={<Protected><VendorDetail /></Protected>} />
            <Route path="/vendors/:code" element={<Protected><VendorDetail /></Protected>} />
            <Route path="/warehouse" element={<Protected><WarehouseAsrs /></Protected>} />
            <Route path="/deliveries" element={<Protected><Deliveries /></Protected>} />
            <Route path="/picking" element={<Protected><Picking /></Protected>} />
            <Route path="/quality" element={<Protected><Quality /></Protected>} />
            <Route path="/quality/lot/:id" element={<Protected><InspectionLotDetail /></Protected>} />
            <Route path="/finance" element={<Protected><Finance /></Protected>} />
            <Route path="/month-end" element={<Protected><MonthEndClose /></Protected>} />
            <Route path="/pick-confirmations" element={<Protected><PickConfirmations /></Protected>} />
            <Route path="/print-center" element={<Protected><PrintCenter /></Protected>} />
            <Route path="/scheduling" element={<Protected><DeliveryScheduling /></Protected>} />
            <Route path="/waste-compliance" element={<Protected><WasteCompliance /></Protected>} />
            <Route path="/controlling" element={<Protected><Controlling /></Protected>} />
            <Route path="/edi" element={<Protected><Edi /></Protected>} />
            <Route path="/copilot" element={<Protected><Copilot /></Protected>} />
            <Route path="/transaction-codes" element={<Protected><TransactionCodes /></Protected>} />
            <Route path="/transport" element={<Protected><TransportPlanning /></Protected>} />
            <Route path="/driver" element={<Protected><DriverApp /></Protected>} />
            <Route path="/warehouse-monitor" element={<Protected><WarehouseMonitor /></Protected>} />
            <Route path="/order-import" element={<Protected><OrderImport /></Protected>} />
            <Route path="/store-directory" element={<Protected><StoreDirectory /></Protected>} />
            <Route path="/pim" element={<Protected><Pim /></Protected>} />
            <Route path="/support-inbox" element={<Protected><SupportInbox /></Protected>} />
            <Route path="/shop" element={<Shop />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
